import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  Rating,
  Paper,
  IconButton,
} from '@mui/material';
import {
  Search,
  LocationOn,
  Star,
  Verified,
  ArrowForward,
  PlayArrow,
  CheckCircle,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const Home = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [location, setLocation] = useState('');
  
  const { ref: heroRef, inView: heroInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  
  const { ref: servicesRef, inView: servicesInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  
  const { ref: professionalsRef, inView: professionalsInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const popularServices = [
    {
      id: 1,
      name: 'Limpieza del Hogar',
      icon: '🏠',
      professionals: 245,
      avgPrice: '$80.000/servicio',
      rating: 4.8,
    },
    {
      id: 2,
      name: 'Plomería',
      icon: '🔧',
      professionals: 189,
      avgPrice: '$120.000/servicio',
      rating: 4.7,
    },
    {
      id: 3,
      name: 'Electricidad',
      icon: '⚡',
      professionals: 156,
      avgPrice: '$150.000/servicio',
      rating: 4.9,
    },
    {
      id: 4,
      name: 'Jardinería',
      icon: '🌱',
      professionals: 134,
      avgPrice: '$70.000/servicio',
      rating: 4.6,
    },
    {
      id: 5,
      name: 'Pintura',
      icon: '🎨',
      professionals: 98,
      avgPrice: '$100.000/servicio',
      rating: 4.8,
    },
    {
      id: 6,
      name: 'Carpintería',
      icon: '🪚',
      professionals: 87,
      avgPrice: '$180.000/servicio',
      rating: 4.9,
    },
  ];

  const featuredProfessionals = [
    {
      id: 1,
      name: 'María González',
      service: 'Limpieza del Hogar',
      rating: 4.9,
      reviews: 127,
      image: '/api/placeholder/150/150',
      verified: true,
      price: '$80.000/servicio',
      location: 'Bogotá',
    },
    {
      id: 2,
      name: 'Carlos Rodríguez',
      service: 'Plomería',
      rating: 4.8,
      reviews: 89,
      image: '/api/placeholder/150/150',
      verified: true,
      price: '$120.000/servicio',
      location: 'Medellín',
    },
    {
      id: 3,
      name: 'Ana Martínez',
      service: 'Electricidad',
      rating: 5.0,
      reviews: 156,
      image: '/api/placeholder/150/150',
      verified: true,
      price: '$150.000/servicio',
      location: 'Cali',
    },
  ];

  const features = [
    {
      icon: '🔍',
      title: 'Encuentra Profesionales',
      description: 'Busca y compara profesionales verificados en tu área',
    },
    {
      icon: '💬',
      title: 'Comunícate Directamente',
      description: 'Chatea con profesionales antes de contratar',
    },
    {
      icon: '💳',
      title: 'Pagos Seguros',
      description: 'Realiza pagos seguros a través de nuestra plataforma',
    },
    {
      icon: '⭐',
      title: 'Califica y Reseña',
      description: 'Comparte tu experiencia y ayuda a otros usuarios',
    },
  ];

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchTerm) params.append('search', searchTerm);
    if (location) params.append('location', location);
    navigate(`/services?${params.toString()}`);
  };

  return (
    <Box>
      {/* Hero Section */}
      <Box
        ref={heroRef}
        sx={
          {
            background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 25%, #16213e 50%, #0f3460 75%, #533483 100%)',
            color: 'white',
            py: { xs: 8, md: 12 },
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.15) 0%, transparent 50%)',
              pointerEvents: 'none',
            },
          }
        }
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h2"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 'bold',
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    lineHeight: 1.2,
                  }}
                >
                  Encuentra el profesional perfecto para tu hogar
                </Typography>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 4,
                    opacity: 0.9,
                    fontSize: { xs: '1.2rem', md: '1.5rem' },
                  }}
                >
                  Conectamos a clientes con profesionales verificados para servicios del hogar
                </Typography>
                
                {/* Search Bar */}
                <Paper
                  elevation={3}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    backgroundColor: 'white',
                  }}
                >
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={5}>
                      <TextField
                        fullWidth
                        placeholder="¿Qué servicio necesitas?"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search color="action" />
                            </InputAdornment>
                          ),
                        }}
                        variant="outlined"
                        size="medium"
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        fullWidth
                        placeholder="Ubicación"
                        value={location}
                        onChange={(e) => setLocation(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationOn color="action" />
                            </InputAdornment>
                          ),
                        }}
                        variant="outlined"
                        size="medium"
                      />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <Button
                        fullWidth
                        variant="contained"
                        size="large"
                        onClick={handleSearch}
                        sx={{
                          py: 1.5,
                          borderRadius: 2,
                          fontWeight: 'bold',
                        }}
                      >
                        Buscar
                      </Button>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    position: 'relative',
                    textAlign: 'center',
                  }}
                >
                  <motion.div
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={heroInView ? { scale: 1, opacity: 1 } : {}}
                    transition={{ duration: 1, delay: 0.3 }}
                  >
                    <Box
                      component="img"
                      src="/api/placeholder/500/400"
                      alt="Profesionales trabajando"
                      sx={{
                        width: '100%',
                        maxWidth: 500,
                        height: 'auto',
                        borderRadius: 3,
                        boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                      }}
                    />
                  </motion.div>
                </Box>
              </Grid>
            </Grid>
          </motion.div>
        </Container>
      </Box>

      {/* Popular Services Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <motion.div
          ref={servicesRef}
          initial={{ opacity: 0, y: 50 }}
          animate={servicesInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
            sx={{ fontWeight: 'bold', mb: 6 }}
          >
            Servicios Populares
          </Typography>
          
          <Grid container spacing={3}>
            {popularServices.map((service, index) => (
              <Grid item xs={12} sm={6} md={4} key={service.id}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                >
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 24px rgba(0,0,0,0.15)',
                      },
                    }}
                    onClick={() => navigate(`/services?category=${service.name}`)}
                  >
                    <CardContent sx={{ textAlign: 'center', p: 3 }}>
                      <Typography
                        variant="h2"
                        sx={{ fontSize: '3rem', mb: 2 }}
                      >
                        {service.icon}
                      </Typography>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ fontWeight: 'bold' }}
                      >
                        {service.name}
                      </Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Rating value={service.rating} readOnly size="small" />
                        <Typography variant="body2" color="text.secondary">
                          {service.rating}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {service.professionals} profesionales
                      </Typography>
                      <Chip
                        label={service.avgPrice}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>

      {/* Featured Professionals Section */}
      <Box sx={{ backgroundColor: 'grey.50', py: 8 }}>
        <Container maxWidth="lg">
          <motion.div
            ref={professionalsRef}
            initial={{ opacity: 0, y: 50 }}
            animate={professionalsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <Typography
              variant="h3"
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{ fontWeight: 'bold', mb: 6 }}
            >
              Profesionales Destacados
            </Typography>
            
            <Grid container spacing={4}>
              {featuredProfessionals.map((professional, index) => (
                <Grid item xs={12} md={4} key={professional.id}>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={professionalsInView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-5px)',
                          boxShadow: '0 8px 16px rgba(0,0,0,0.1)',
                        },
                      }}
                      onClick={() => navigate(`/professionals/${professional.id}`)}
                    >
                      <Box sx={{ position: 'relative', textAlign: 'center', pt: 3 }}>
                        <Avatar
                          src={professional.image}
                          alt={professional.name}
                          sx={{
                            width: 80,
                            height: 80,
                            mx: 'auto',
                            mb: 2,
                            border: 3,
                            borderColor: 'primary.main',
                          }}
                        />
                        {professional.verified && (
                          <Verified
                            color="primary"
                            sx={{
                              position: 'absolute',
                              top: 20,
                              right: 20,
                            }}
                          />
                        )}
                      </Box>
                      
                      <CardContent sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                          {professional.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          {professional.service}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Rating value={professional.rating} readOnly size="small" />
                          <Typography variant="body2">
                            {professional.rating} ({professional.reviews})
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          📍 {professional.location}
                        </Typography>
                        <Chip
                          label={professional.price}
                          color="primary"
                          size="small"
                        />
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
            
            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Button
                variant="outlined"
                size="large"
                endIcon={<ArrowForward />}
                onClick={() => navigate('/professionals')}
                sx={{ px: 4, py: 1.5 }}
              >
                Ver Todos los Profesionales
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography
          variant="h3"
          component="h2"
          textAlign="center"
          gutterBottom
          sx={{ fontWeight: 'bold', mb: 6 }}
        >
          ¿Por qué elegir Jooru?
        </Typography>
        
        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <Typography
                    variant="h2"
                    sx={{ fontSize: '3rem', mb: 2 }}
                  >
                    {feature.icon}
                  </Typography>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.description}
                  </Typography>
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          py: 8,
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h3"
                component="h2"
                gutterBottom
                sx={{ fontWeight: 'bold' }}
              >
                ¿Eres un profesional?
              </Typography>
              <Typography
                variant="h6"
                sx={{ mb: 4, opacity: 0.9 }}
              >
                Únete a nuestra plataforma y conecta con clientes que necesitan tus servicios
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => navigate('/register?type=professional')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderRadius: 3,
                }}
              >
                Registrarse como Profesional
              </Button>
            </Box>
          </motion.div>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;